import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { ChatMessage } from '../../hooks/types';

interface MessageSearchBarProps {
    messages: ChatMessage[];
    onNavigateToMessage: (messageId: string) => void;
    onClose: () => void;
    onHighlightChange: (messageId: string | null) => void;
}

export const MessageSearchBar: React.FC<MessageSearchBarProps> = ({
    messages,
    onNavigateToMessage,
    onClose,
    onHighlightChange
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    // Filtra mensagens que contêm o termo de busca
    const searchResults = useMemo(() => {
        if (!searchTerm.trim()) return [];
        const lowerTerm = searchTerm.toLowerCase();
        return messages.filter(msg =>
            msg.text?.toLowerCase().includes(lowerTerm)
        );
    }, [messages, searchTerm]);

    // Foco no input ao abrir
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Navega até a mensagem atual quando o índice muda
    useEffect(() => {
        if (searchResults.length > 0 && searchResults[currentIndex]) {
            const messageId = searchResults[currentIndex].id;
            onNavigateToMessage(messageId);
            onHighlightChange(messageId);
        } else {
            onHighlightChange(null);
        }
    }, [currentIndex, searchResults, onNavigateToMessage, onHighlightChange]);

    // Reset índice quando busca muda
    useEffect(() => {
        setCurrentIndex(0);
    }, [searchTerm]);

    // Limpa highlight ao fechar
    useEffect(() => {
        return () => onHighlightChange(null);
    }, [onHighlightChange]);

    const goToPrevious = useCallback(() => {
        if (searchResults.length > 0) {
            setCurrentIndex(prev =>
                prev > 0 ? prev - 1 : searchResults.length - 1
            );
        }
    }, [searchResults.length]);

    const goToNext = useCallback(() => {
        if (searchResults.length > 0) {
            setCurrentIndex(prev =>
                prev < searchResults.length - 1 ? prev + 1 : 0
            );
        }
    }, [searchResults.length]);

    // Atalhos de teclado
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            } else if (e.key === 'Enter') {
                if (e.shiftKey) {
                    goToPrevious();
                } else {
                    goToNext();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose, goToPrevious, goToNext]);

    return (
        <div className="px-4 py-2 bg-white">
            <div className="flex items-center gap-2 bg-surface-subtle rounded-full px-4 py-2 border border-blue-500">
                {/* Ícone de busca */}
                <AppIcon name="search" className="w-4 h-4 text-muted flex-shrink-0" />

                {/* Input */}
                <input
                    ref={inputRef}
                    type="search"
                    id="message-search-input"
                    name="message-search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar mensagens..."
                    aria-label="Buscar mensagens"
                    autoComplete="off"
                    className="flex-1 bg-transparent border-none focus:ring-0 focus:outline-none text-base placeholder-muted text-default"
                />

                {/* Contador - apenas quando tem busca */}
                {searchTerm && (
                    <span className="chat-count text-muted">
                        {searchResults.length > 0
                            ? `${currentIndex + 1}/${searchResults.length}`
                            : '0/0'
                        }
                    </span>
                )}

                {/* Navegação - apenas quando tem resultados */}
                {searchTerm && searchResults.length > 0 && (
                    <>
                        <button
                            onClick={goToPrevious}
                            className="p-1 text-muted hover:text-primary rounded-full transition-colors"
                            title="Anterior"
                        >
                            <AppIcon name="chevron-up" className="w-4 h-4" />
                        </button>
                        <button
                            onClick={goToNext}
                            className="p-1 text-muted hover:text-primary rounded-full transition-colors"
                            title="Próximo"
                        >
                            <AppIcon name="chevron-down" className="w-4 h-4" />
                        </button>
                    </>
                )}

                {/* Botão fechar */}
                <button
                    onClick={onClose}
                    className="p-1 text-muted hover:text-secondary rounded-full transition-colors"
                    title="Fechar"
                >
                    <AppIcon name="x" className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};
